import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import Spinner from 'common/Spinner';
import useKeyboardControls from 'hooks/useKeyboardControls';
import { withStyles } from 'theme/withStyles';
import ErrorMsg from 'common/ErrorMsg';
import FlyupLabel from 'common/FlyupLabel';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import fieldActions from 'core/fields/actions';
import uiActions from 'core/ui/actions';
import dataActions from 'core/data/actions';
import visuallyHidden from 'styles/visuallyHidden';

import rootStyles from './styles/root';
import inputStyles from './styles/input';

const styles = ({ forms, oaoButtons: buttons, palette, shadows, spacing, typography }) => ({
  icon: {
    display: 'none',
  },
  input: inputStyles({ forms, palette, typography }),
  list: {
    '& .selected': {
      background: buttons.primary.background,
      color: buttons.primary.color,
    },
    '& li': {
      '&:hover': {
        background: buttons.primary.background,
        color: buttons.primary.color,
        cursor: 'pointer',
      },
      padding: [2, spacing.unit],
    },
    background: palette.background,
    border: `1px solid ${palette.form.border}`,
    borderRadius: 2,
    fontFamily: typography.fontFamilies.secondary,
    margin: 0,
    maxHeight: 200,
    overflowY: 'auto',
    padding: 0,
    zIndex: 10,
  },
  listContainer: {
    boxShadow: shadows.tooltip,
    margin: [-1, 0, 0, 0],
    position: 'absolute',
    top: '100%',
    width: '100%',
    zIndex: 10,
  },
  root: rootStyles({ spacing }),
  typeAhead: {
    position: 'relative',
  },
  visuallyHidden,
});

const DELAY = 200;

let getOptionsTimeout;
const debouncedGetOptions = (getOptions, targetValue) => {
  if (getOptionsTimeout) {
    clearTimeout(getOptionsTimeout);
    getOptionsTimeout = null;
  }
  getOptionsTimeout = setTimeout(() => {
    getOptionsTimeout = null;
    getOptions(targetValue);
  }, DELAY);
};

const getValue = (id, options) => {
  if (!options || (options && options.length === 0) || !id) {
    return '';
  }
  for (let i = 0; i < options.length; i += 1) {
    if (options[i].id === id) {
      return options[i].name;
    }
  }
  return '';
};

const AutoComplete = ({
  classes,
  error,
  focus,
  getOptions,
  help,
  loading,
  name,
  onBlur,
  onChange,
  onFocus,
  onPaste,
  options,
  placeholder,
  required,
  setMenuOpen,
  tooltip,
  validate,
  value,
}) => {
  const [typedValue, setTypedValue] = useState(getValue(value, options));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const clicked = useRef(false);
  const inputRef = useRef(null);
  const inputId = `type-ahead-${name}`;
  const enterCallback = useCallback(
    (choices, index) => {
      setTypedValue(choices[index].name);
      setOptionsOpen(false);
      onChange(choices[index]);
      inputRef.current.focus();
    },
    [onChange],
  );
  const [selectedIndex, events] = useKeyboardControls({
    defaultKeyUpCallback: () => {
      setOptionsOpen(true);
    },
    enterCallback,
    keyUpCallback: (choices, index) => {
      if (choices[index]) {
        setTypedValue(choices[index].name);
      }
    },
    options,
  });
  useEffect(() => {
    setTypedValue(getValue(value, options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(() => {
    setMenuOpen(optionsOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsOpen]);
  return (
    <>
      <div className={classnames('form-group', { error }, classes.root)}>
        <Spinner loading={loading} />
        <div className="region-auto-complete">
          <div className={classnames('type-ahead form-group', classes.typeAhead)}>
            <FlyupLabel
              fieldName={name}
              error={error}
              value={value}
              className="type-ahead-label"
              htmlFor={inputId}
              required={required}
              tooltip={tooltip}
            />
            <div
              className={classnames('type-ahead-input-wrapper')}
              role="combobox"
              aria-expanded={optionsOpen && options && options.length > 0}
              aria-owns={`${inputId}-listbox`}
              aria-haspopup="listbox"
              id={`${inputId}-combobox`}
              aria-controls={`${inputId}-listbox`}
            >
              <input
                autoCapitalize="none"
                autoComplete="off"
                aria-autocomplete="list"
                ref={inputRef}
                id={inputId}
                name={inputId}
                aria-activedescendant={
                  selectedIndex !== undefined ? `${inputId}-item${selectedIndex}` : undefined
                }
                aria-describedby={classnames(`autocomplete-help-${name}`, {
                  [`help-${name}`]: help,
                  [`error-${name}`]: error,
                })}
                aria-invalid={error}
                type="text"
                inputMode="search"
                className={classnames('form-control js-type-ahead-input', classes.input)}
                placeholder={placeholder}
                value={typedValue}
                onChange={(e) => {
                  const { value: targetValue } = e.target;
                  setTypedValue(targetValue);
                  debouncedGetOptions(getOptions, targetValue);
                  clicked.current = false;
                }}
                onFocus={() => {
                  setOptionsOpen(true);
                  onFocus();
                }}
                onBlur={() =>
                  setTimeout(() => {
                    if (typedValue && options) {
                      const [optionSelected] = options.filter(
                        (o) =>
                          o.name.match(new RegExp(typedValue.trim(), 'gi')) &&
                          o.name.length === typedValue.trim().length,
                      );
                      if (
                        optionSelected &&
                        optionSelected.name.length === typedValue.trim().length
                      ) {
                        setTypedValue(optionSelected.name);
                        onChange(optionSelected);
                      } else if (value && !optionSelected && !clicked.current) {
                        onChange(null);
                      }
                    } else {
                      onChange(null);
                    }

                    setOptionsOpen(false);
                    onBlur();
                    validate();
                  }, DELAY)
                }
                onKeyUp={events.onKeyUp}
                onPaste={onPaste}
              />
              <div
                className={classnames('sr-only', classes.visuallyHidden)}
                id={`autocomplete-help-${name}`}
              >
                Provides auto-suggestions when entering text
              </div>
              <span className={classnames('icon-search', classes.icon)} />
            </div>
            <div
              className={classnames(
                'type-ahead-list-container region-type-ahead-list',
                classes.listContainer,
                {
                  on: focus || typedValue,
                },
              )}
            >
              {optionsOpen && options && options.length > 0 && (
                <>
                  <ul
                    id={`type-ahead-list${inputId}`}
                    className={classnames('type-ahead-list', classes.list)}
                    role="listbox"
                  >
                    {options &&
                      options.length > 0 &&
                      options.map((option, optionIndex) => (
                        /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
                        <li
                          role="option"
                          key={option.id}
                          id={`${inputId}-item${optionIndex}`}
                          className={classnames('type-ahead-list-item', {
                            selected: selectedIndex === optionIndex,
                          })}
                          data-qa={option.id}
                          aria-selected={selectedIndex === optionIndex}
                          onClick={(e) => {
                            e.preventDefault();
                            clicked.current = true;
                            enterCallback(options, optionIndex);
                          }}
                          onKeyUp={(e) => {
                            if (e.keyCode === 13 || e.keyCode === 32) {
                              e.preventDefault();
                              clicked.current = true;
                              enterCallback(options, optionIndex);
                            }
                          }}
                        >
                          {option.name}
                        </li>
                      ))}
                  </ul>
                  <div role="alert" className={classes.visuallyHidden}>
                    {`${options.length} results available.`}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <Tooltip tooltip={tooltip} labeledBy={`label-${name}`} />
        <Help help={help} id={`help-${name}`} />
      </div>

      <ErrorMsg name={name} />
    </>
  );
};

AutoComplete.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  focus: PropTypes.bool.isRequired,
  getOptions: PropTypes.func.isRequired,
  help: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  placeholder: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  setMenuOpen: PropTypes.func.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]).isRequired,
};

AutoComplete.defaultProps = {
  options: null,
};

const AutoCompleteStyled = withStyles(styles)(AutoComplete);

const mapStateToProps = ({ fields, ui }, { name }) => {
  const field = fields[name];
  return {
    focus: ui.fieldFocus === name,
    options:
      !field.selectorValues && field.value && field.valueDescr
        ? [
            {
              id: field.value,
              name: field.valueDescr,
            },
          ]
        : field.selectorValues,
  };
};

const mapDispatchToProps = (dispatch, { id, name }) => ({
  getOptions: (targetValue) => {
    dispatch(fieldActions.fetchAutoComplete(id, targetValue, name));
  },
  onChange: (options) => {
    dispatch(dataActions.changeAutoComplete(name, options));
  },
  setMenuOpen: (options) => {
    dispatch(uiActions.setMenuOpen(options));
  },
});

export default withFieldProps(mapStateToProps, mapDispatchToProps)(AutoCompleteStyled);
